import { message } from "antd";
import { useMemo, useState } from "react";
import { Info, Minus, Plus } from "lucide-react";
import { useUrlState } from "utils";
import { useProduct } from "../hooks/use-products";
import { ProductType } from "_graphql/__generated__/globalTypes";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { ICartProduct } from "utils/types";
import { addProductToCart } from "../../../redux/slice/cart-slice";

type Props = {};

const AddProductToCartForm = (props: Props) => {
  const dispatch = useAppDispatch();
  const cartProducts = useAppSelector((state) => state.cart.cart.products);
  const [, setModal] = useUrlState("modal");
  const [id, setId] = useUrlState("id");
  const { product, loading } = useProduct({
    variables: {
      filter: {
        id: id ? { eq: id } : undefined,
      },
    },
  });

  // const [moreProfit, setMoreProfit] = useState(false);
  // const [customProfit, setCustomProfit] = useState<string>("");
  const [profit, setProfit] = useState(0);
  const [qty, setQty] = useState(1);

  const basePrice = Number(product?.price || 0);

  // const suggestedProfits = useMemo(
  //   () => [
  //     Math.round(basePrice * 0.1),
  //     Math.round(basePrice * 0.15),
  //     Math.round(basePrice * 0.2),
  //   ],
  //   [basePrice]
  // );

  // const selectedProfit = (amount: number) => {
  //   setProfit(amount);
  //   setCustomProfit(amount.toString());
  // };

  // const handleCustomProfitChange = (
  //   e: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const value = e.target.value;
  //   if (/^\d*$/.test(value)) {
  //     setCustomProfit(value);
  //     setProfit(value ? Number(value) : 0);
  //   }
  // };

  const itemTotal = basePrice * qty;
  const totalWithProfit = itemTotal + profit;

  const inc = () => {
    if (qty < Number(product?.qty || Infinity)) {
      setQty(qty + 1);
    }
  };

  const dec = () => {
    if (qty > 1) {
      setQty(qty - 1);
    }
  };

  const ChangeQty = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = +e.target.value;
    const moq = product?.minimumOrderQuantity || 1;

    if (inputValue < moq) {
      message.error(
        "Quantity should be same or more than minimum order quantity"
      );
      return;
    }

    if (inputValue > Number(product?.qty || Infinity)) {
      message.error("Quantity exceeds available stock");
      return;
    }

    setQty(inputValue);
  };

  const closeModal = () => {
    setId(undefined);
    setModal(undefined);
  };

  const existsInCart = cartProducts?.find(
    (p) => p.productSKU === product?.sku
  );

  const handleAddToCart = () => {
    if (!product) return;

    if (
      existsInCart &&
      Number(existsInCart.productQty) + qty > Number(product.qty || Infinity)
    ) {
      message.error("Product quantity limit reached", 5);
      return;
    }

    const productObj: ICartProduct = {
      imageLink: product.avatar,
      name: product.title,
      productPrice: product.price,
      newPrice: product.price,
      productQty: qty,
      productSKU: product.sku,
      profitAdded: profit,
      currency: product.currency ?? undefined,
      availableQty: product.qty ?? undefined,
      shippingCost: 0,
    };

    dispatch(addProductToCart(productObj));
    message.success("Product added to cart", 5);
    closeModal();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-gray-500 text-sm">Loading product...</span>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-gray-500 text-sm">Product not found.</span>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        <div className="flex items-start space-x-2 bg-blue-50 text-blue-700 text-xs p-3">
          <Info size={16} className="mt-0.5" />
          <p>
            You can only add product to cart if you are ordering multiple
            items for the same customer. Else checkout as a single order.
          </p>
        </div>

        {/* Product Section */}
        <div className="p-4 flex space-x-4 border-b">
          <img
            src={product.avatar}
            alt={product.title}
            className="w-28 h-28 object-cover rounded-lg"
          />
          <div className="flex-1">
            <h3 className="text-gray-900 font-semibold">{product.title}</h3>
            {product.productType === ProductType.Digital && (
              <p className="text-gray-500 text-sm">Type: Digital</p>
            )}
            <p className="text-gray-900 font-bold mt-2">
              {product.currency} {product.price}
            </p>

            {/* Quantity controls */}
            {product.productType === ProductType.Physical && (
              <div className="flex items-center space-x-3 mt-2">
                <button
                  type="button"
                  title="Decrease"
                  onClick={dec}
                  disabled={qty <= 1}
                  className={`w-7 h-7 flex items-center justify-center border rounded-full transition-colors ${
                    qty <= 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Minus className="h-4 w-4" />
                </button>

                <input
                  type="number"
                  id="productQty"
                  name="productQty"
                  value={qty}
                  onChange={ChangeQty}
                  max={
                    Number(product.qty) ||
                    Number(product.minimumOrderQuantity)
                  }
                  className="w-16 text-center text-gray-900 font-medium border-gray-300 rounded-md focus:ring-tendo-active focus:border-tendo-active sm:text-sm appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                />

                <button
                  type="button"
                  title="Increase"
                  onClick={inc}
                  className="w-7 h-7 flex items-center justify-center border rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profit Section */}
        <div className="p-4 space-y-3 border-b">
          {/* <div>
            <h4 className="text-gray-900 font-medium">Add your profit</h4>
            <p className="text-sm text-gray-600">
              You've added {product.currency}{" "}
              {parseFloat(profit.toString()).toFixed(2)} as a profit to this
              product
            </p>
          </div> */}

          {/* Preset profit options */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Suggested Profit
            </label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {suggestedProfits.map((amount, index) => (
                <button
                  key={index}
                  type="button"
                  title="profit buttons"
                  className={`py-2 px-4 text-sm font-medium rounded-md ${
                    profit === amount
                      ? "bg-buttonPrimary text-white"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                  onClick={() => selectedProfit(amount)}
                >
                  {product.currency} {amount}
                </button>
              ))}
            </div>
          </div> */}

          {/* Custom profit input */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Profit
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {product.currency}
                </span>
              </div>
              <input
                type="text"
                name="custom-profit"
                id="custom-profit"
                value={customProfit}
                onChange={handleCustomProfitChange}
                className="focus:ring-buttonPrimary focus:border-buttonPrimary block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md border"
                placeholder="0"
              />
            </div>
            {moreProfit && (
              <span className="text-xs font-medium text-yellow-500 mt-1 block">
                We advice you add profit up to 100% of product price.
              </span>
            )}
          </div> */}

          {product.combination_string && (
            <div className="mb-3">
              <label htmlFor="spec" className="block text-sm font-medium">
                Product spec
              </label>
              <div className="mt-2 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="spec"
                  autoComplete="spec"
                  className="focus:ring-sokoBlue focus:border-sokoBlue block w-full pl-7 pr-12 py-2 sm:text-sm border-gray-300 border-2 rounded-md placeholder:text-gray-200 disabled:bg-gray-200"
                  placeholder="Black small"
                  value={product.combination_string.replace("&", "-")}
                  disabled
                />
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="p-4 text-sm text-gray-700 space-y-1 border-b">
          <div className="flex justify-between">
            <span>Item ({qty}x)</span>
            <span>
              {product.currency} {itemTotal.toFixed(2)}
            </span>
          </div>
          {/* <div className="flex justify-between">
            <span>My Profit</span>
            <span>
              {product.currency} {profit.toFixed(2)}
            </span>
          </div> */}
          <div className="flex justify-between font-semibold text-gray-900 border-t pt-2">
            <span>Total</span>
            <span>
              {product.currency} {totalWithProfit.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-4 space-y-2">
          {product.productType === ProductType.Physical && (
            <button
              type="button"
              onClick={handleAddToCart}
              className="w-full border-2 border-primary-600 text-primary-600 py-2.5 rounded-lg font-medium hover:bg-primary-600 hover:text-white transition-colors"
            >
              Add to Cart
            </button>
          )}
          {/* <button
            type="button"
            onClick={handleAddToCart}
            className="w-full bg-[#0B3768] text-white py-2.5 rounded-lg font-medium hover:bg-[#0d447f] transition-colors"
          >
            Order Now
          </button> */}
        </div>
      </div>
    </>
  );
};

export default AddProductToCartForm;
